import BackBar from '../components/BackBar';
import { useLocation } from 'react-router-dom';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { allergenLabel, listWords } from '@verlo/shared/lib/allergens';
import { useState, useEffect, useCallback, useRef } from 'react';
import { serviceApi, kitchenApi, printingApi, kitchenScreensApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import ManagerOverrideDialog from '../components/order/ManagerOverrideDialog';
import ScreenStationDialog from '../components/kitchen/ScreenStationDialog';
import { screenId as kitchenScreenId, cachedStation, cacheStation } from '../lib/kitchenScreen';
import wsService, { WS_EVENTS } from '@verlo/shared/services/websocket';
import { groupByCourse } from '../lib/courses';
import { useSearchParams } from 'react-router-dom';
import { kdsAction } from '../lib/kdsStatus';
import { boardSignatures, boardNeedsAttention, minutesSince, elsewhereWords } from '../lib/kitchenBoard';
import { onStation } from '@verlo/shared/lib/kitchenRoutes';
import StationBoard from '../components/kitchen/StationBoard';
import { lineThrough, routeOf, atWords, lateness, withTick } from '../lib/stationBoard';
import { Card, CardContent } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import { toast } from 'sonner';
import { 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  RefreshCw, 
  Volume2, 
  VolumeX, 
  AlertTriangle,
  Wifi,
  WifiOff,
  Printer,
  Undo2,
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'new', tone: 'warn' },
  in_progress: { label: 'cooking', tone: 'info' },
  ready: { label: 'ready', tone: 'ok' },
};

// One board component, two stations (FOH plan round two, F4): `/kitchen` shows the
// kitchen's tickets, `/bar-board` the bar's — the same cards, the bar's vocabulary.
const STATION = {
  kitchen: { title: 'Kitchen', cooking: 'COOKING', cookingEmpty: 'Nothing on', ready: 'READY', readyEmpty: 'Nothing ready', clear: 'Nothing on the pass.' },
  bar:     { title: 'Bar Display',     cooking: 'MAKING', cookingEmpty: 'Nothing being made', ready: 'READY', readyEmpty: 'Nothing waiting', clear: 'Nothing at the bar.' },
};

// How long the header offers the last bump back. Long enough to notice a plate went to the
// wrong table, short enough that the button isn't a fixture.
const RECALL_FOR_MS = 2 * 60 * 1000;
const agoWords = (ms) => (ms < 60000 ? 'just now' : `${Math.floor(ms / 60000)}m ago`);

const KitchenDisplayPage = ({ station = 'kitchen' }) => {
  const words = STATION[station] || STATION.kitchen;
  // The kitchen's own stations (grill, pastry …) from the venue's settings. Pick one and the
  // board shows the tickets that carry a line for it, the other stations' lines faint — the
  // pass still bumps the whole ticket. A manager sets which station a screen shows, on the
  // screen (M2, Oct 2026), and the till keeps it against the screen's own id; the back office
  // lists the screens. `?station=grill` still pins a screen, as it did before.
  const [searchParams, setSearchParams] = useSearchParams();
  const from = useLocation().state?.from || null;
  const [kitchenStations, setKitchenStations] = useState([]);
  const [stationsLoaded, setStationsLoaded] = useState(false);
  const { hasPermission } = useAuth();
  // Moving a kitchen ticket on is the kitchen's (Oct 2026): the Kitchen role, shift leads and
  // managers. Anyone else sees the board as it is, to know where a table's food is, and runs
  // food from the Orders board. The bar board is whoever's on the bar (D3).
  const mayMove = station === 'bar' || hasPermission('kitchen.bump');
  const [screen, setScreen] = useState(() => ({ station: cachedStation(), name: '' }));
  const [choosing, setChoosing] = useState(false);
  const [stepUp, setStepUp] = useState(null);     // the choice waiting on a manager's PIN
  const [savingScreen, setSavingScreen] = useState(false);
  useEffect(() => {
    if (station !== 'kitchen') return;
    kitchenScreensApi.get(kitchenScreenId())
      .then((r) => { setScreen({ station: r.data?.station || null, name: r.data?.name || '' }); cacheStation(r.data?.station || null); })
      .catch(() => { /* the till out of reach: the last station this screen showed */ });
  }, [station]);
  // A saved station that's since gone (removed in the back office, or the list didn't load)
  // shows the Pass rather than an empty board nobody can change from here.
  const wanted = searchParams.get('station') || screen.station || 'kitchen';
  const stillThere = !stationsLoaded || wanted === 'kitchen' || kitchenStations.some((st) => st.key === wanted);
  const picked = station === 'kitchen' ? (stillThere ? wanted : 'kitchen') : station;
  const stationName = (key) => (key === 'kitchen' ? 'The Pass' : kitchenStations.find((st) => st.key === key)?.name || key);
  const saveScreen = useCallback(async (choice, creds) => {
    setSavingScreen(true);
    try {
      const { data } = await kitchenScreensApi.set(kitchenScreenId(), {
        station: choice.station, name: choice.name,
        ...(creds ? { manager_id: creds.managerId, manager_pin: creds.pin } : {}),
      });
      setScreen({ station: data.station, name: data.name || '' });
      cacheStation(data.station);
      if (searchParams.get('station')) setSearchParams({});
      setChoosing(false);
      setStepUp(null);
      toast.success(`This screen shows ${data.station === 'kitchen' ? 'the Pass' : stationName(data.station)} now`);
    } finally {
      setSavingScreen(false);
    }
  }, [searchParams, setSearchParams, kitchenStations]); // eslint-disable-line react-hooks/exhaustive-deps
  // A manager signed in changes it straight away; anyone else gets a manager to step up.
  const chooseScreen = async (st, name) => {
    const choice = { station: st, name };
    if (!hasPermission('epos.manager_override')) { setChoosing(false); setStepUp(choice); return; }
    try { await saveScreen(choice); } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't change this screen"); }
  };
  const queryStation = picked;
  // One of the kitchen's own stations (not the pass, not the bar): its screen shows, and
  // sounds for, its own lines.
  const ownStation = station === 'kitchen' && picked !== 'kitchen' ? picked : null;
  useEffect(() => {
    if (station !== 'kitchen') return;
    serviceApi.venueSettings()
      .then((r) => setKitchenStations(r.data?.kitchen_stations || []))
      .catch(() => setKitchenStations([]))
      .finally(() => setStationsLoaded(true));
  }, [station]);
  // The board is tickets, not orders: one card per send to the kitchen (backend kitchen.py).
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  // `wsService.connect()` below returns early if the socket is already open, so a
  // component that mounts onto an already-live connection (navigating back to the KDS)
  // never sees a `CONNECTION` event to flip this — start from the socket's actual state.
  const [wsConnected, setWsConnected] = useState(() => wsService.isConnected());
  // What the board last asked of the kitchen, ticket by ticket. Null until the first load,
  // which opens the screen rather than announcing anything (lib/kitchenBoard.js).
  const seenRef = useRef(null);
  // The board's own clock. A ticket's age is the one number a kitchen uses to spot a late
  // one, and it was computed only when something re-rendered — with the socket up the
  // fallback poll is off, so on a quiet pass nothing did, and a ticket fired at 15:21 still
  // read "4m" at 15:27. Twenty seconds is fine-grained enough for whole minutes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(t);
  }, []);
  const audioContextRef = useRef(null);

  // Play notification sound
  const playNewOrderSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      // Reuse or create AudioContext
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => oscillator.stop(), 200);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, [soundEnabled]);

  // Fetch the open tickets. A ticket is one send — a table that adds fries later has two
  // cards, and bumping the mains never touches the fries. Drinks are filtered per card below,
  // as before: the bar makes those.
  const fetchTickets = useCallback(async () => {
    try {
      const res = await kitchenApi.listTickets(true, queryStation);
      const open = res.data;
      // One place decides the sound: a ticket arrived, or one already up changed what it
      // asks of the kitchen. A discount, a comp or this board's own bump changes neither.
      // What was seen is kept with the screen it was seen on: picking another station is
      // a screen opening, not a board full of new tickets, and it used to sound as one.
      const seen = seenRef.current?.station === queryStation ? seenRef.current.tickets : null;
      if (boardNeedsAttention(seen, open, ownStation)) playNewOrderSound();
      seenRef.current = { station: queryStation, tickets: boardSignatures(open, ownStation) };
      setTickets(open);
    } catch (err) {
      console.error('Failed to fetch kitchen tickets');
    } finally {
      setLoading(false);
    }
  }, [playNewOrderSound, queryStation, ownStation]);

  // WebSocket event handlers
  useEffect(() => {
    wsService.connect();

    const unsubConnection = wsService.subscribe(WS_EVENTS.CONNECTION, (data) => {
      setWsConnected(data.status === 'connected');
    });

    // A new order fires ticket #1; an update with added lines fires the next ticket. Both
    // are "something new for the kitchen" — sound, then refetch the board.
    const unsubNewOrder = wsService.subscribe(WS_EVENTS.ORDER_CREATED, () => fetchTickets());
    // Fired by eleven endpoints, eight of them money. The refetch tells which mattered.
    const unsubOrderUpdated = wsService.subscribe(WS_EVENTS.ORDER_UPDATED, () => fetchTickets());
    // Another screen bumped a ticket (or a line was struck off one): silent refetch.
    const unsubTicket = wsService.subscribe(WS_EVENTS.TICKET_UPDATED, () => fetchTickets());
    const unsubOrderStatus = wsService.subscribe(WS_EVENTS.ORDER_STATUS_CHANGED, () => fetchTickets());

    fetchTickets();

    // Fallback polling (in case WebSocket fails)
    const pollInterval = setInterval(() => {
      if (!wsService.isConnected()) fetchTickets();
    }, 15000);

    return () => {
      unsubConnection();
      unsubNewOrder();
      unsubOrderUpdated();
      unsubTicket();
      unsubOrderStatus();
      clearInterval(pollInterval);
    };
  }, [fetchTickets, playNewOrderSound]);

  // The last ticket bumped off the pass, so a mis-tap isn't a lost ticket: Undo on the
  // toast for ten seconds, then a Recall button in the header for two minutes after.
  const [lastBump, setLastBump] = useState(null);   // { id, name, at }
  const recall = useCallback(async (bump) => {
    try {
      await kitchenApi.updateTicketStatus(bump.id, 'ready');
      setLastBump(null);
      toast.success(`${bump.name} is back on the pass`);
      fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't bring that ticket back");
    }
  }, [fetchTickets]);

  // Start / Ready / Done on one ticket. The order's own progress rolls up server-side.
  const bumpTicket = useCallback(async (ticketId, newStatus) => {
    const name = tickets.find((t) => t.id === ticketId)?.table_name || 'Ticket';
    try {
      await kitchenApi.updateTicketStatus(ticketId, newStatus);
      // Optimistic update
      setTickets(prev => {
        if (newStatus === 'delivered') return prev.filter(t => t.id !== ticketId);
        return prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t);
      });
      if (newStatus === 'delivered') {
        const bump = { id: ticketId, name, at: Date.now() };
        setLastBump(bump);
        toast(`${name} off the pass`, { duration: 10000, action: { label: 'Undo', onClick: () => recall(bump) } });
      } else {
        toast.success('Ticket updated');
      }
    } catch (err) {
      toast.error('Failed to update ticket');
      fetchTickets(); // Revert optimistic update
    }
  }, [fetchTickets, recall, tickets]);

  // A station ticks a dish off, or takes the tick back. Shown at once; the server says
  // whether it stands (it refuses a dish that isn't at this station yet).
  const tick = useCallback(async (ticket, line, done) => {
    const stamp = new Date().toISOString();
    setTickets((prev) => prev.map((t) => (t.id !== ticket.id ? t : {
      ...t,
      status: done && t.status === 'pending' ? 'in_progress' : t.status,
      items: withTick(t.items, line, picked, done, stamp),
    })));
    try {
      await kitchenApi.tickStation(ticket.id, line.line_id, picked, done, line.menu_item_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't tick that off");
      fetchTickets();
    }
  }, [picked, fetchTickets]);

  // This ticket, again, on paper — what a pass asks for when a roll runs out mid-ticket.
  // It prints marked *** REPRINT *** so nobody makes the dish twice.
  const reprint = useCallback(async (ticketId) => {
    try {
      const { data } = await printingApi.reprintTicket(ticketId);
      toast.success(data.queued > 1 ? `Printing again on ${data.queued} printers` : 'Printing again');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not print it again');
    }
  }, []);

  // Calculate time since order
  const getTimeSinceOrder = useCallback((firedAt) => minutesSince(firedAt, now), [now]);

  const oldestAge = (list) => Math.max(...list.map((t) => getTimeSinceOrder(t.fired_at)));
  const pendingTickets = tickets.filter(t => t.status === 'pending');
  const preparingTickets = tickets.filter(t => t.status === 'in_progress');
  const readyTickets = tickets.filter(t => t.status === 'ready');

  // One ticket = one card. "#2" and the add-on tag tell the kitchen this is a later send for
  // a table already on the board, so nobody goes looking for a card they already bumped.
  const TicketCard = ({ ticket }) => {
    const status = ticket.status;
    const config = statusConfig[status];
    const hasAllergies = ticket.has_allergies;
    const minutesAgo = getTimeSinceOrder(ticket.fired_at);
    const allVoided = (ticket.items || []).length > 0 && ticket.items.every((l) => l.voided);
    const isUrgent = status === 'pending' && minutesAgo > 5 && !allVoided;

    const { nextStatus, actionLabel } = kdsAction(status, allVoided);

    // Drinks never reach this board: a send is split by station server-side (kitchen.py),
    // and this page asks for the kitchen's tickets only. Every line here is the kitchen's.
    const foodItems = ticket.items || [];
    if (foodItems.length === 0) return null;
    // A station's screen shows the lines it works on, anywhere on their route, and one line
    // about the rest of the ticket: the grill needs to know the fryer has two to do, not to
    // read them.
    const stationScreen = station === 'kitchen' && picked !== 'kitchen';
    const shownItems = stationScreen ? foodItems.filter((l) => onStation(l, picked)) : foodItems;
    const elsewhere = stationScreen ? elsewhereWords(foodItems.filter((l) => !l.voided && !onStation(l, picked)), kitchenStations) : '';
    const tableAllergens = ticket.allergens || [];
    const clashing = foodItems.some((l) => !l.voided && l.allergen_clash?.length);
    // A line the till could not read against the table's allergy: a word on the dish it
    // doesn't know, or a dish that is not on the menu to be read.
    const unchecked = foodItems.some((l) => !l.voided && l.allergen_unread?.length);
    const isAddOn = ticket.sequence > 1;
    // On the pass of a kitchen with stations: where each dish is, and how much of the table
    // is through. Ready is outlined until every dish is (option A's Pass).
    const routed = station === 'kitchen' && !stationScreen && kitchenStations.length > 0;
    const live = foodItems.filter((l) => !l.voided);
    // A dish the Pass makes itself is through once the Pass ticks it (M1).
    const through = live.filter(lineThrough).length;
    const allThrough = live.length > 0 && through === live.length;

    return (
      <Card className={`bg-white border shadow-none ${clashing || unchecked ? 'border-2 border-signal-stop-line' : 'border-hairline'} ${isUrgent && !routed ? 'ring-2 ring-signal-stop-line animate-pulse' : ''}`} data-testid={`ticket-${ticket.id}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-hairline">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-heading font-bold text-[15px] truncate">{ticket.table_name || 'Bar'}</span>
            <span className="text-xs font-mono text-ink-faint">#{ticket.sequence}</span>
            {isAddOn && (
              <Signal tone="warn" dot={false}>add-on</Signal>
            )}
            {status === 'pending' ? (
              // New is ink, not copper (M3): copper is for time running out.
              <span className="inline-flex items-center gap-1.5 border border-ink bg-white px-2 py-1 font-heading text-[10.5px] font-bold uppercase leading-none tracking-[0.05em] text-ink" data-testid="kds-new-tag">
                <i aria-hidden className="h-1.5 w-1.5 shrink-0 bg-ink" />{config.label}
              </span>
            ) : (
              <Signal tone={config.tone}>{status === 'in_progress' ? words.cooking : config.label}</Signal>
            )}
            {hasAllergies && (
              <AlertTriangle size={14} className="text-signal-warn shrink-0" />
            )}
          </div>
          <div className={`flex items-center gap-2 text-xs shrink-0 ${
            routed ? (lateness(minutesAgo) === 'very-late' ? 'text-signal-stop font-bold' : lateness(minutesAgo) === 'late' ? 'text-signal-warn font-bold' : 'text-ink-soft')
              : isUrgent ? 'text-signal-stop font-bold' : 'text-ink-soft'}`}>
            <Clock size={12} />
            {minutesAgo}m
          </div>
        </div>

        {/* Items */}
        <CardContent className="px-3 pb-2 pt-2">
          {/* What the table has said (30 Sep 2026). A house message never reaches a
              ticket, so everything here is for whoever is cooking — and an allergy reads
              first, above every instruction, because it is the one that cannot wait. */}
          {hasAllergies && (ticket.allergy_notes || tableAllergens.length > 0) && (
            <Notice tone="stop" word="Allergy" className="mb-2 px-2.5 py-1.5 text-xs" data-testid="ticket-allergy">
              {[tableAllergens.map(allergenLabel).join(', '), ticket.allergy_notes].filter(Boolean).join(' · ')}
              {/* Said, not left to be inferred, but only as far as the till knows it. What
                  the till knows is the menu's list for each dish: so this says the dishes
                  don't *list* it, never that they don't contain it, and says nothing at all
                  when there is a line it couldn't read. A dish nobody has filled in lists
                  nothing, and that is not the same as being safe. */}
              {tableAllergens.length > 0 && !clashing && !unchecked && (
                <span className="block text-ink-soft" data-testid="ticket-no-clash">
                  No dish on this ticket lists {listWords(tableAllergens)}
                </span>
              )}
            </Notice>
          )}
          {[...(ticket.messages || [])].sort((a, b) => (a.kind === 'allergy' ? 0 : 1) - (b.kind === 'allergy' ? 0 : 1)).map((m) => (
            <Notice key={m.id} tone={m.kind === 'allergy' ? 'stop' : 'warn'}
                    word={m.kind === 'allergy' ? 'Allergy' : 'Table'}
                    className="mb-2 px-2.5 py-1.5 text-xs" data-testid={`ticket-message-${m.kind}`}>
              {m.text}
            </Notice>
          ))}

          {allVoided && (
            <Notice tone="info" word="Taken back" className="mb-2 px-2.5 py-1.5 text-xs" data-testid="ticket-all-voided">
              Nothing to make: everything on this ticket came off the order.
            </Notice>
          )}
          {/* Food items — a line struck off after firing stays visible, crossed out, so the
              kitchen sees the void rather than wondering where it went. */}
          <div className="space-y-1 mb-2">
            {groupByCourse(shownItems).map((g, gi, all) => (
              <div key={g.course}>
                {/* A course header only when the ticket spans more than one course. */}
                {all.length > 1 && (
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink-faint mt-1 mb-0.5" data-testid={`ticket-course-${g.course}`}>{g.label}</div>
                )}
                {g.lines.map(({ line: item, index: idx }) => (
              <div key={idx} className={`text-sm ${item.voided ? 'line-through text-ink-faint' : ''}`} data-testid={item.voided ? 'ticket-line-voided' : 'ticket-line'} data-station={item.station || 'kitchen'}>
                <div className="flex items-start">
                  <span className={`font-bold w-6 ${item.voided ? 'text-ink-faint' : 'text-ink-soft'}`}>{item.voided ? '✕' : `${item.quantity}×`}</span>
                  <div className="flex-1">
                    <span className={`font-medium ${item.voided ? 'text-ink-faint' : 'text-ink'}`}>{item.name}</span>
                    {routed && !item.voided && !routeOf(item).length && mayMove && (
                      // Made here at the Pass (M1): the Pass ticks it, and a second tap takes it back.
                      <button
                        type="button"
                        onClick={() => tick(ticket, item, !lineThrough(item))}
                        className={`float-right ml-2 inline-flex items-center gap-1.5 border px-2 py-0.5 text-[12px] ${lineThrough(item) ? 'border-ink bg-ink text-white' : 'border-hairline-strong bg-white text-ink'}`}
                        data-testid="pass-line-make"
                      >
                        {lineThrough(item) ? '✓ ready' : 'make at the Pass'}
                      </button>
                    )}
                    {routed && !item.voided && !routeOf(item).length && !mayMove && (
                      <span className={`float-right ml-2 text-[12px] ${lineThrough(item) ? 'text-ink' : 'text-ink-faint'}`} data-testid="pass-line-at">
                        {lineThrough(item) ? '✓ ready' : 'at the Pass'}
                      </span>
                    )}
                    {routed && !item.voided && routeOf(item).length > 0 && (
                      <span className={`float-right ml-2 text-[12px] ${lineThrough(item) ? 'text-ink' : 'text-ink-faint'}`} data-testid="pass-line-at">
                        {lineThrough(item) ? '✓ ready' : atWords(item, kitchenStations)}
                      </span>
                    )}
                    {/* Part of a meal. Without it the bar gets a lone lemonade and nobody
                        knows why — and a chef plating the strips cannot see the fries are
                        the same child's. */}
                    {item.part_of && (
                      <span className="ml-1.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-ink-faint" data-testid="line-part-of">
                        {item.part_of}{item.meal_group ? ` · ${item.meal_group}` : ''}
                      </span>
                    )}
                    {item.modifiers?.length > 0 && (
                      <div className="text-xs text-signal-warn mt-0.5">
                        {item.modifiers.map((mod, i) => (
                          // A typed instruction prints in caps (NO CELERY) and reads louder than a choice.
                          <span key={i} className={`block ${String(mod) === String(mod).toUpperCase() && /[A-Z]/.test(String(mod)) ? 'font-bold text-ink' : ''}`}>• {mod}</span>
                        ))}
                      </div>
                    )}
                    {item.notes && (
                      <div className="text-xs text-ink-soft italic mt-0.5">
                        Note: {item.notes}
                      </div>
                    )}
                    {/* The table can't have something in this dish. Worked out by the server
                        from the menu when it was sent, so it is here whatever the till did. */}
                    {!item.voided && item.allergen_clash?.length > 0 && (
                      <div className="mt-1 text-xs font-bold uppercase tracking-[0.04em] text-signal-stop" data-testid="line-clash">
                        Contains {listWords(item.allergen_clash)}
                      </div>
                    )}
                    {/* What the dish lists that the till couldn't read, so couldn't check. */}
                    {!item.voided && item.allergen_unread?.length > 0 && (
                      <div className="mt-1 text-xs font-bold uppercase tracking-[0.04em] text-signal-stop" data-testid="line-check">
                        Check: {item.allergen_unread.join(', ')}
                      </div>
                    )}
                    {/* This dish's own allergy note, louder than its instructions — the
                        table's allergy flag is already on the card's header. */}
                    {item.allergy_notes && (
                      <div className="mt-1 flex items-start gap-1.5" data-testid="line-allergy">
                        <Signal tone="stop">allergy</Signal>
                        <span className="text-xs font-semibold text-signal-stop">{item.allergy_notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
                ))}
              </div>
            ))}
            {elsewhere && (
              <div className="text-xs text-ink-faint" data-testid="ticket-elsewhere">+ {elsewhere}</div>
            )}
          </div>

          {/* Action Button. Beside it, the one thing a pass asks for when a roll runs
              out mid-service: this ticket, again, on paper (#94). */}
          <div className="flex gap-2">
            {mayMove && <Button
              className={`h-9 flex-1 ${routed && !allThrough && status !== 'ready' ? 'border border-hairline-strong bg-white text-ink hover:bg-surface-2' : 'bg-verlo hover:bg-verlo-hover'}`}
              size="sm"
              onClick={() => bumpTicket(ticket.id, nextStatus)}
              data-testid={`ticket-action-${ticket.id}`}
            >
              {actionLabel}
              {allVoided || status === 'ready' ? <CheckCircle size={14} className="ml-1" /> : <ArrowRight size={14} className="ml-1" />}
            </Button>}
            <Button
              className="h-9 w-9 shrink-0"
              size="icon"
              variant="outline"
              title="Print this ticket again"
              onClick={() => reprint(ticket.id)}
              data-testid={`ticket-reprint-${ticket.id}`}
            >
              <Printer size={14} />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen bg-paper">
        <RefreshCw className="animate-spin text-ink-faint" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper p-4" data-testid="kitchen-display">
      {/* Arrived here from another screen (the Orders board, the plan's kitchen, the Bar
          page)? Then a way back. A pinned screen opened at boot has none — it is home. */}
      {from && (
        <div className="mb-4">
          <BackBar title={words.title} fallback={from} />
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {!from && <h1 className="text-2xl font-bold font-heading text-ink">{words.title}</h1>}
          <Badge variant="outline" className="text-sm">
            {tickets.length} open ticket{tickets.length !== 1 ? 's' : ''}
          </Badge>
          {station === 'kitchen' && (kitchenStations.length > 0 || wanted !== 'kitchen') && (
            <div className="flex items-center gap-2 text-sm" data-testid="kds-screen">
              <span className="text-ink-soft">This screen:</span>
              <b className="text-ink" data-testid="kds-screen-station">{stationName(picked)}</b>
              <Button variant="outline" size="sm" onClick={() => setChoosing(true)} data-testid="kds-screen-change">Change</Button>
            </div>
          )}
          {/* WebSocket status indicator */}
          <div className={`flex items-center gap-1 text-xs ${wsConnected ? 'text-signal-ok' : 'text-ink-faint'}`}>
            {wsConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            <span>{wsConnected ? 'Live' : 'Polling'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mayMove && lastBump && now - lastBump.at < RECALL_FOR_MS && (
            <Button variant="outline" onClick={() => recall(lastBump)} data-testid="kds-recall">
              <Undo2 size={15} className="mr-1.5" />
              Recall {lastBump.name}
              <span className="ml-1.5 font-normal text-ink-faint">bumped {agoWords(now - lastBump.at)}</span>
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={soundEnabled ? 'text-ink' : 'text-ink-faint'}
            title={soundEnabled ? 'Mute notifications' : 'Enable notifications'}
          >
            {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </Button>
          {/* When the socket is live the board updates itself — the manual refresh is only
              a fallback while polling / disconnected. */}
          {!wsConnected && (
            <Button
              variant="outline"
              size="icon"
              onClick={fetchTickets}
              title="Refresh tickets"
            >
              <RefreshCw size={16} />
            </Button>
          )}
        </div>
      </div>

      {!mayMove && (
        <Notice tone="info" word="See only" className="mb-4" data-testid="kds-see-only">
          You can see where each table's food is. The kitchen, shift leads and managers move tickets on.
        </Notice>
      )}

      {/* An empty board is one calm line, not three empty boxes (FOH plan round two, I). */}
      {tickets.length === 0 && !loading && (
        // Read from across a kitchen: in the middle of the screen, not its top fifth.
        <div className="mx-auto mt-[18vh] max-w-xl border border-hairline border-l-[3px] border-l-signal-ok-line bg-white px-6 py-10 text-center" data-testid="kds-all-clear">
          <p className="font-heading text-lg font-bold text-ink">All clear</p>
          <p className="text-sm text-ink-soft mt-1">{words.clear} New tickets appear here the moment they're sent.</p>
        </div>
      )}

      {/* A station's own screen (option A): one card per table, a tap per dish. */}
      {ownStation && tickets.length > 0 && (
        <StationBoard tickets={tickets} station={ownStation} stations={kitchenStations} now={now} onTick={mayMove ? tick : undefined} />
      )}

      {/* Order Columns */}
      <div className={`grid grid-cols-3 gap-3 ${tickets.length === 0 || ownStation ? 'hidden' : ''}`}>
        {/* NEW */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            {/* Ink, not copper (M3): copper means time is running out, and new isn't late. */}
            <span className="w-2 h-2 rounded-full bg-ink"></span>
            <span className="text-sm font-medium text-ink-soft">NEW ({pendingTickets.length})</span>
            {pendingTickets.length > 0 && <span className="ml-auto font-mono text-xs text-ink-faint">oldest {oldestAge(pendingTickets)}m</span>}
          </div>
          <div className="space-y-2">
            {pendingTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
            {pendingTickets.length === 0 && (
              <div className="text-center py-8 text-ink-faint text-sm bg-white rounded-lg">
                No new tickets
              </div>
            )}
          </div>
        </div>

        {/* COOKING */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-signal-info-line"></span>
            <span className="text-sm font-medium text-ink-soft">{words.cooking} ({preparingTickets.length})</span>
            {preparingTickets.length > 0 && <span className="ml-auto font-mono text-xs text-ink-faint">longest {oldestAge(preparingTickets)}m</span>}
          </div>
          <div className="space-y-2">
            {preparingTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
            {preparingTickets.length === 0 && (
              <div className="text-center py-8 text-ink-faint text-sm bg-white rounded-lg">
                {words.cookingEmpty}
              </div>
            )}
          </div>
        </div>

        {/* READY */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-signal-ok-line"></span>
            <span className="text-sm font-medium text-ink-soft">READY ({readyTickets.length})</span>
            {readyTickets.length > 0 && <span className="ml-auto font-mono text-xs text-ink-faint">waiting {oldestAge(readyTickets)}m</span>}
          </div>
          <div className="space-y-2">
            {readyTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
            {readyTickets.length === 0 && (
              <div className="text-center py-8 text-ink-faint text-sm bg-white rounded-lg">
                Nothing ready
              </div>
            )}
          </div>
        </div>
      </div>

      <ScreenStationDialog
        open={choosing}
        onOpenChange={setChoosing}
        stations={kitchenStations}
        current={picked}
        currentName={screen.name}
        busy={savingScreen}
        onSave={chooseScreen}
      />
      {/* Someone who isn't a manager is signed in: a manager steps up with their PIN. */}
      <ManagerOverrideDialog
        open={!!stepUp}
        onOpenChange={(v) => { if (!v) setStepUp(null); }}
        title="A manager changes this screen"
        description={stepUp ? `Show ${stationName(stepUp.station)} on this screen.` : ''}
        warning="Only a manager can change what a kitchen screen shows. It's logged."
        confirmLabel="Change screen"
        onSubmit={(creds) => saveScreen(stepUp, creds)}
      />
    </div>
  );
};

export default KitchenDisplayPage;
